import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Select } from 'primeng/select';
import {
    CategoryType,
    GameTemplate,
    SiteTemplate,
    WinMode,
} from '@/lib-backend';
import { SiteTemplateService } from '@/app/services/site-template.service';
import {
    ART_GROUP,
    ART_PROP,
    pageProp,
    ProductAssetService,
    readArtProperty,
} from '@/app/services/product-asset.service';
import { SharedContentService } from '@/app/services/shared-content.service';
import { NotificationService } from '@/app/services/notification.service';
import { AuthService } from '@/app/services/auth.service';
import { BrandKit, BrandKitService } from '@/app/services/brand-kit.service';
import {
    AiAssistApiError,
    AiAssistService,
    PageCandidate,
    PagesResult,
} from '@/app/services/ai-assist.service';
import { ImagePreviewService } from '@/app/services/image-preview.service';
import {
    hashPathToken,
    sharedContentPreviewUrl,
} from '@/app/utils/shared-content-url';

const PAGE_COUNT = 3;
const SAMPLES = 2;

interface RefOption {
    siteTemplateId: string;
    name: string;
    brandKit: BrandKit | null;
    iconPlate: string | null;
    bannerPlate: string | null;
}

interface PageView {
    index: number;
    scene: string;
    cta: string;
    candidates: PageCandidate[];
    selectedUrl: string | null;
}

/**
 * Generate the game-template **default** storefront pages (portrait promo
 * images, Contract 3) borrowing a reference site template's style. Scenes and
 * marketing copy are auto-authored by Grok. The chosen pages are saved as one
 * finalized `pages` variant (name = title) and their ordered URLs emitted so
 * the host can set the template's default `Images[]`.
 */
@Component({
    selector: 'app-ai-pages-generate',
    standalone: true,
    imports: [
        FormsModule,
        NgClass,
        Button,
        Dialog,
        Message,
        ProgressSpinner,
        Select,
    ],
    templateUrl: './ai-pages-generate.html',
})
export class AiPagesGenerate {
    readonly visible = input.required<boolean>();
    readonly template = input<GameTemplate | null>(null);
    /**
     * Realm mode: lock generation to one site template (the realm's) and carry
     * the realm id into the pages call. Null = template-default mode.
     */
    readonly fixedSiteTemplateId = input<string | null>(null);
    readonly realmId = input<string | null>(null);
    /**
     * Realm-level title override. When set (non-null), it is the name the
     * operator entered for this game on the realm and takes precedence over the
     * template title for generation context and the saved variant name. Null =
     * use the template title (template-default mode).
     */
    readonly titleOverride = input<string | null>(null);

    readonly visibleChange = output<boolean>();
    /** Ordered stored page URLs, on save. */
    readonly applied = output<string[]>();

    private readonly siteTemplateService = inject(SiteTemplateService);
    private readonly productAsset = inject(ProductAssetService);
    private readonly sharedContent = inject(SharedContentService);
    private readonly notification = inject(NotificationService);
    private readonly auth = inject(AuthService);
    private readonly brandKitService = inject(BrandKitService);
    private readonly aiAssist = inject(AiAssistService);
    protected readonly preview = inject(ImagePreviewService);

    protected readonly phase = signal<'config' | 'results'>('config');
    protected readonly preparing = signal(false);
    protected readonly generating = signal(false);
    protected readonly saving = signal(false);
    protected readonly genError = signal<string | null>(null);

    protected readonly options = signal<RefOption[]>([]);
    protected readonly refSiteTemplateId = signal<string | null>(null);
    protected readonly pages = signal<PageView[]>([]);

    private abort: AbortController | null = null;

    protected readonly canManage = computed(() => this.auth.isGlobalAdmin());

    /** The name to generate with: realm override if set, else template. */
    protected readonly effectiveTitle = computed(
        () => this.titleOverride() ?? this.template()?.Title ?? '',
    );

    private readonly compatibleCategory = computed<CategoryType | null>(() => {
        switch (this.template()?.GameKind?.WinMode) {
            case WinMode.SkillBased:
                return CategoryType.SkillBasedGames;
            case WinMode.ChanceBased:
                return CategoryType.LuckyBasedGames;
            default:
                return null;
        }
    });

    /** Realm mode hides the reference picker (the site template is fixed). */
    protected readonly lockedRef = computed(() => !!this.fixedSiteTemplateId());

    protected readonly refOptions = computed(() =>
        this.options().map((o) => ({ label: o.name, value: o.siteTemplateId })),
    );

    protected readonly selectedRef = computed<RefOption | null>(
        () =>
            this.options().find(
                (o) => o.siteTemplateId === this.refSiteTemplateId(),
            ) ?? null,
    );

    protected readonly blockReason = computed<string | null>(() => {
        if (!this.canManage()) return 'Only a global admin can generate pages.';
        if (this.compatibleCategory() === null) {
            return "This game's win mode is unresolved — no compatible site template.";
        }
        const ref = this.selectedRef();
        if (!ref) return 'Select a reference site template.';
        if (!ref.brandKit || ref.brandKit.references.length === 0) {
            return 'The reference template has no brand references to generate from.';
        }
        if (this.aiAssist.status() === 'down') {
            return 'AI Assist service is unavailable.';
        }
        return null;
    });

    protected readonly canSave = computed(
        () =>
            !this.saving() &&
            !this.generating() &&
            this.pages().length > 0 &&
            this.pages().every((p) => !!p.selectedUrl),
    );

    constructor() {
        effect(() => {
            if (this.visible()) {
                void this.prepare();
                if (this.aiAssist.status() === 'unknown') {
                    void this.aiAssist.refreshHealth();
                }
                void this.aiAssist.refreshVendorBalances();
            } else {
                this.cancelPolling();
            }
        });
    }

    private async prepare(): Promise<void> {
        this.reset();
        const template = this.template();
        const wanted = this.compatibleCategory();
        if (!template || wanted === null) return;

        this.preparing.set(true);
        try {
            if (this.siteTemplateService.templates().length === 0) {
                await this.siteTemplateService.loadTemplates();
            }
            const fixed = this.fixedSiteTemplateId();
            const compatible = this.siteTemplateService
                .templates()
                .filter((st: SiteTemplate) => st.CategoryType === wanted)
                // Realm mode: only the realm's own site template.
                .filter((st: SiteTemplate) => !fixed || st.SiteTemplateId === fixed)
                .slice()
                .sort((a, b) => a.Name.localeCompare(b.Name));

            const options = await Promise.all(
                compatible.map(async (st) => {
                    const [brandKit, art] = await Promise.all([
                        this.brandKitService.resolve(st.SiteTemplateId),
                        this.productAsset.getGameTemplateBaseArt(
                            st.SiteTemplateId,
                            template.Id,
                        ),
                    ]);
                    const option: RefOption = {
                        siteTemplateId: st.SiteTemplateId,
                        name: st.Name,
                        brandKit,
                        iconPlate: art
                            ? readArtProperty(art, ART_PROP.Icon)
                            : null,
                        bannerPlate: art
                            ? readArtProperty(art, ART_PROP.Banner)
                            : null,
                    };
                    return option;
                }),
            );
            if (!this.visible()) return;
            this.options.set(options);
            const ready = options.find(
                (o) => o.brandKit && o.brandKit.references.length > 0,
            );
            this.refSiteTemplateId.set(
                (ready ?? options[0])?.siteTemplateId ?? null,
            );
        } finally {
            this.preparing.set(false);
        }
    }

    protected async generate(): Promise<void> {
        const template = this.template();
        const ref = this.selectedRef();
        if (!template || !ref || this.blockReason() || this.generating()) return;
        const kit = ref.brandKit;
        if (!kit) return;

        this.cancelPolling();
        this.abort = new AbortController();
        const signal = this.abort.signal;
        this.generating.set(true);
        this.genError.set(null);
        this.pages.set([]);
        try {
            // The game's own base plates ride along as extra references (text-free
            // → fetched inline as base64) to keep pages on-brand for the game.
            const gameBaseB64: string[] = [];
            for (const plate of [ref.iconPlate, ref.bannerPlate]) {
                if (!plate) continue;
                const b64 = await this.fetchAsB64(sharedContentPreviewUrl(plate));
                if (b64) gameBaseB64.push(b64);
            }
            const accepted = await this.aiAssist.generatePages({
                jobId: crypto.randomUUID(),
                realmId: this.realmId(),
                templateId: ref.siteTemplateId,
                version: kit.version ?? '',
                styleDescriptor: kit.style,
                referenceImagesB64: kit.references.map((r) => r.b64),
                gameBaseB64,
                gameTemplateId: template.Id,
                gameKind: template.GameKind?.Name ?? template.GameKindId,
                gameMeta: {
                    name: this.effectiveTitle(),
                    description: template.Description,
                },
                title: this.effectiveTitle(),
                sceneMode: 'auto',
                pageCount: PAGE_COUNT,
                copyMode: 'auto',
                samples: SAMPLES,
            });
            const done = await this.aiAssist.awaitJob(accepted.job_id, {
                signal,
                timeoutMs: 360_000,
            });
            const result = done.result as unknown as PagesResult | null;
            const pages = (result?.pages ?? [])
                .slice()
                .sort((a, b) => a.index - b.index)
                .map((p) => ({
                    index: p.index,
                    scene: p.scene,
                    cta: p.cta_text,
                    candidates: p.candidates,
                    selectedUrl: p.candidates[0]?.url ?? null,
                }));
            this.pages.set(pages);
            this.phase.set('results');
        } catch (err) {
            if (!signal.aborted) this.genError.set(this.friendlyError(err));
        } finally {
            this.generating.set(false);
        }
    }

    protected pick(index: number, candidate: PageCandidate): void {
        this.pages.update((list) =>
            list.map((p) =>
                p.index === index ? { ...p, selectedUrl: candidate.url } : p,
            ),
        );
    }

    protected isSelected(index: number, url: string): boolean {
        return this.pages().find((p) => p.index === index)?.selectedUrl === url;
    }

    protected backToConfig(): void {
        this.cancelPolling();
        this.phase.set('config');
        this.pages.set([]);
    }

    protected async save(): Promise<void> {
        const template = this.template();
        const ref = this.selectedRef();
        if (!template || !ref || !this.canSave()) return;
        const ordered = this.pages();
        this.saving.set(true);
        try {
            const variantId = crypto.randomUUID();
            const storedUrls: string[] = [];
            for (let i = 0; i < ordered.length; i++) {
                const url = ordered[i].selectedUrl;
                if (!url) {
                    this.saving.set(false);
                    return;
                }
                const stored = await this.rehost(
                    template.Id,
                    ref.siteTemplateId,
                    variantId,
                    i + 1,
                    url,
                );
                if (!stored) {
                    this.notification.show({
                        severity: 'error',
                        summary: 'Could not save the generated pages',
                    });
                    return;
                }
                storedUrls.push(stored);
            }
            const props = [
                { Name: ART_PROP.Group, Value: ART_GROUP.Pages },
                ...storedUrls.map((u, i) => ({
                    Name: pageProp(i + 1),
                    Value: u,
                })),
                { Name: ART_PROP.Name, Value: this.effectiveTitle() },
            ];
            const { result } =
                await this.productAsset.addGameTemplateFinalizedArt(
                    ref.siteTemplateId,
                    template.Id,
                    props,
                );
            if (result === 'Ok') {
                this.notification.show({
                    severity: 'success',
                    summary: 'Pages generated',
                    detail: 'Saved as a finalized variant and applied as the default images.',
                });
                this.applied.emit(storedUrls);
                this.onVisibleChange(false);
            } else {
                this.notification.show({
                    severity: 'error',
                    summary: 'Save failed',
                    detail:
                        result === 'SiteTemplateNotFound'
                            ? 'The reference site template no longer exists.'
                            : 'Please try again.',
                });
            }
        } finally {
            this.saving.set(false);
        }
    }

    private async rehost(
        gameTemplateId: string,
        siteTemplateId: string,
        variantId: string,
        n: number,
        candidateUrl: string,
    ): Promise<string | null> {
        const hashed = await hashPathToken(siteTemplateId);
        const path = `products/${gameTemplateId}/${hashed}/${variantId}-page-${n}`;
        return this.sharedContent.rehostImageUrl(path, candidateUrl);
    }

    protected onVisibleChange(value: boolean): void {
        if (!value) this.cancelPolling();
        this.visibleChange.emit(value);
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    private cancelPolling(): void {
        this.abort?.abort();
        this.abort = null;
    }

    private reset(): void {
        this.cancelPolling();
        this.phase.set('config');
        this.options.set([]);
        this.refSiteTemplateId.set(null);
        this.pages.set([]);
        this.genError.set(null);
        this.generating.set(false);
        this.saving.set(false);
    }

    private async fetchAsB64(url: string): Promise<string | null> {
        try {
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) return null;
            const bytes = new Uint8Array(await res.arrayBuffer());
            let binary = '';
            for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        } catch {
            return null;
        }
    }

    private friendlyError(err: unknown): string {
        if (!(err instanceof AiAssistApiError)) {
            return 'Could not reach the AI Assist service.';
        }
        switch (err.code) {
            case 'vendor_timeout':
                return 'Generation took too long. Try fewer samples.';
            case 'vendor_unavailable':
                return 'The image model is temporarily unavailable.';
            case 'content_moderated':
                return 'Blocked by the content filter — try again.';
            case 'invalid_references':
                return 'Reference images could not be used.';
            case 'unsupported_scene':
                return 'An unsupported scene was requested.';
            case 'invalid_request':
                return err.message ?? 'The request was rejected.';
            default:
                return err.message ?? 'Something went wrong.';
        }
    }
}
